import { xor, difference, capitalize } from 'lodash';
import {
  ALL_RECIPES,
  ALL_PRODUCT_TYPES,
  productTypeReference,
} from './constants';

export const getDefaultSubscriptionProduct = subscriptionProducts => {
  // We start by looking for the 4 week box and search down.
  // We _should_ always find something before exiting the loop.
  // If we don't, worst case, starter box == subscription box.
  let numWeeks = 4;

  while (numWeeks) {
    const productForWeeks = subscriptionProducts.find(
      // eslint-disable-next-line no-loop-func
      product => product.weeks_of_food === numWeeks,
    );

    if (productForWeeks) {
      return productForWeeks;
    }

    numWeeks--;
  }

  // This should never happen but ensures we always have something to work with.
  return subscriptionProducts[0];
};

export const getStarterBoxProduct = fresh => fresh.starterBoxes[0];

export const getStarterBoxes = ({ fresh, dry, mixed }) => ({
  fresh: fresh.starterBoxes[0],
  dry: dry.starterBoxes[0],
  mixed: mixed.starterBoxes[0],
});

export const getUnavailableRecipes = availableRecipes =>
  ALL_RECIPES.filter(recipe => !availableRecipes.includes(recipe));
export const getUnavailableProductTypes = availableProductTypes =>
  ALL_PRODUCT_TYPES.filter(
    productType => !availableProductTypes.includes(productType),
  );

export const getPetAllergicToOwnPlan = (petPlan, availableRecipes) => {
  if (!petPlan) {
    return false;
  }

  const allergicToFresh =
    availableRecipes.fresh &&
    !petPlan.variants[0].recipes.fresh.every(recipe =>
      availableRecipes.fresh.includes(recipe),
    );
  const allergicToDry =
    availableRecipes.dry &&
    !petPlan.variants[0].recipes.dry.every(recipe =>
      availableRecipes.dry.includes(recipe),
    );

  // If pet's current recipe combo isn't in available recipes,
  // they're allergic
  return Boolean(allergicToFresh || allergicToDry);
};

export const getNumStarterBoxWeeks = recommendedStarterBox =>
  recommendedStarterBox.weeks_of_food;

export const getTotalPacks = (hasPlan, petPlan, recommendedVariant) =>
  hasPlan ? petPlan.tray_count : recommendedVariant.tray_count;

export const getSuggestedVariant = (hasPlan, petPlan, recommended) =>
  hasPlan ? petPlan.variants[0] : recommended;

export const getVariantByRecipes = (variants, recipes) => {
  const variant = variants.find(
    variant =>
      !xor(variant.recipes.fresh, recipes.fresh).length &&
      !xor(variant.recipes.dry, recipes.dry).length,
  );
  return variant;
};

export const getStarterBoxFreshConfig = (
  fresh,
  petPlan,
  selectedFreshRecipes,
) => {
  const availableRecipes = fresh.availableRecipes.fresh;
  const recommended = fresh.recommended;
  const starterBoxProduct = getStarterBoxProduct(fresh);
  const config = getFreshConfig(
    availableRecipes,
    recommended,
    starterBoxProduct,
    petPlan,
  );
  return {
    ...config,
    defaultRecipes: selectedFreshRecipes ?? config.defaultRecipes,
    subscriptionProducts: fresh.products,
    numStarterBoxWeeks: getNumStarterBoxWeeks(starterBoxProduct),
  };
};

export const getCurrentProductTypeConfig = (
  { fresh, dry, mixed },
  petProduct,
) => {
  const availableProductTypes = [];
  if (fresh.availableRecipes.fresh.length) availableProductTypes.push('fresh');
  if (dry.availableRecipes.dry.length) availableProductTypes.push('dry');
  if (mixed.availableRecipes.fresh.length || mixed.availableRecipes.dry.length)
    availableProductTypes.push('mixed');

  const { defaultProductType, unavailableProductTypes } = getProductTypesConfig(
    availableProductTypes,
    petProduct,
  );

  return {
    defaultProductType,
    unavailableProductTypes,
    subscriptionProducts: {
      fresh: fresh.products,
      dry: dry.products,
      mixed: mixed.products,
    },
    currentFrequency: petProduct.weeks_of_food,
  };
};

export const getStarterBoxProductTypeConfig = (
  { fresh, dry, mixed },
  petProduct,
  selectedProductType,
) => {
  const availableProductTypes = [];
  if (fresh.availableRecipes.fresh.length) availableProductTypes.push('fresh');
  if (dry.availableRecipes.dry.length) availableProductTypes.push('dry');
  if (mixed.availableRecipes.fresh.length || mixed.availableRecipes.dry.length)
    availableProductTypes.push('mixed');
  const config = getProductTypesConfig(availableProductTypes, petProduct);
  return {
    ...config,
    defaultProductType: selectedProductType ?? config.defaultProductType,
    subscriptionProducts: {
      fresh: fresh.products,
      dry: dry.products,
      mixed: mixed.products,
    },
  };
};

export const getProductConfigByFrequency = (
  productTypes,
  subscriptionProducts,
  unavailableProductTypes,
  currentFrequency,
) => {
  const recommendedFreshFrequency = subscriptionProducts.fresh.find(product =>
    product.variants.find(
      variant =>
        variant.sku === productTypes.fresh.recommended.subscription_sku,
    ),
  )?.weeks_of_food;
  const recommendedMixedFrequency = subscriptionProducts.mixed.find(product =>
    product.variants.find(
      variant =>
        variant.sku === productTypes.mixed.recommended.subscription_sku,
    ),
  )?.weeks_of_food;
  const recommendedDryFrequency = subscriptionProducts.dry?.find(product =>
    product.variants.find(
      variant => variant.sku === productTypes.dry.recommended?.subscription_sku,
    ),
  )?.weeks_of_food;

  const freshVariants =
    subscriptionProducts.fresh.find(
      product => product.weeks_of_food === currentFrequency,
    )?.variants ||
    subscriptionProducts.fresh.find(
      product => product.weeks_of_food === recommendedFreshFrequency,
    )?.variants ||
    [];

  const mixedVariants =
    subscriptionProducts.mixed.find(
      product => product.weeks_of_food === currentFrequency,
    )?.variants ||
    subscriptionProducts.mixed.find(
      product => product.weeks_of_food === recommendedMixedFrequency,
    )?.variants ||
    [];

  const dryVariants =
    subscriptionProducts.dry.find(
      product => product.weeks_of_food === currentFrequency,
    )?.variants ||
    subscriptionProducts.dry.find(
      product => product.weeks_of_food === recommendedDryFrequency,
    )?.variants ||
    [];

  const products = {
    fresh: !unavailableProductTypes.includes('fresh') ? freshVariants[0] : null,
    mixed: !unavailableProductTypes.includes('mixed') ? mixedVariants[0] : null,
    dry: !unavailableProductTypes.includes('dry') ? dryVariants[0] : null,
    freshVariants,
    mixedVariants,
    dryVariants,
  };

  return products;
};

export const getStarterBoxDryConfig = (dry, petProduct, selectedDryRecipes) => {
  const availableRecipes = dry.availableRecipes.dry;
  const recommended = dry.recommended;
  const starterBoxProduct = getStarterBoxProduct(dry);
  const config = getDryConfig(
    availableRecipes,
    recommended,
    starterBoxProduct,
    petProduct,
  );
  return {
    ...config,
    defaultRecipes: selectedDryRecipes ?? config.defaultRecipes,
    subscriptionProducts: dry.products,
    numStarterBoxWeeks: getNumStarterBoxWeeks(starterBoxProduct),
  };
};

export const getCurrentFreshConfig = (fresh, petPlan) => {
  const availableRecipes = fresh.availableRecipes.fresh;
  const recommended = fresh.recommended;
  const product =
    fresh.products.find(p => p.sku === petPlan.sku) ||
    getDefaultSubscriptionProduct(fresh.products);

  return getFreshConfig(availableRecipes, recommended, product, petPlan);
};

export const getFreshConfig = (
  availableFreshRecipes,
  recommended,
  product,
  petPlan,
) => {
  const hasPlan = !!petPlan;
  const totalPacks = getTotalPacks(hasPlan, petPlan, product);
  const isPetAllergicToOwnPlan = getPetAllergicToOwnPlan(petPlan, {
    fresh: availableFreshRecipes,
  });

  let defaultRecipes;

  if (hasPlan && !isPetAllergicToOwnPlan) {
    defaultRecipes = !petPlan.variants[0].recipes.fresh.length
      ? recommended.recipes.fresh
      : petPlan.variants[0].recipes.fresh;
  } else {
    defaultRecipes = recommended.recipes.fresh;
  }

  const suggestedVariant = getSuggestedVariant(hasPlan, petPlan, recommended);
  const unavailableRecipes = getUnavailableRecipes(availableFreshRecipes);

  return {
    product,
    availableRecipes: availableFreshRecipes,
    totalPacks: Number(totalPacks),
    defaultRecipes,
    suggestedVariant,
    unavailableRecipes,
  };
};

export const getProductTypesConfig = (availableProductTypes, petProduct) => {
  const hasProduct = !!petProduct;

  const defaultProductType = hasProduct // TODO Get rid of this nested ternary if we can.
    ? productTypeReference[petProduct.product_type]
    : availableProductTypes.includes('fresh')
    ? 'fresh'
    : 'dry';

  const unavailableProductTypes = getUnavailableProductTypes(
    availableProductTypes,
  );
  return {
    defaultProductType,
    unavailableProductTypes,
  };
};

export const getCurrentDryConfig = (dry, petPlan) => {
  const availableRecipes = dry.availableRecipes.dry;
  const recommended = dry.recommended;
  const product =
    dry.products.find(p => p.sku === petPlan.sku) ||
    getDefaultSubscriptionProduct(dry.products);

  return getDryConfig(availableRecipes, recommended, product, petPlan);
};

export const getDryConfig = (
  availableBakedRecipes,
  recommended,
  product,
  petPlan,
) => {
  const hasPlan = !!petPlan;
  const totalPacks = getTotalPacks(hasPlan, petPlan, product);
  const isPetAllergicToOwnPlan = getPetAllergicToOwnPlan(petPlan, {
    dry: availableBakedRecipes,
  });

  let defaultRecipes;

  if (hasPlan && !isPetAllergicToOwnPlan) {
    defaultRecipes = !petPlan.variants[0].recipes.dry.length
      ? recommended.recipes.dry
      : petPlan.variants[0].recipes.dry;
  } else {
    defaultRecipes = recommended.recipes.dry;
  }

  const suggestedVariant = getSuggestedVariant(hasPlan, petPlan, recommended);
  const unavailableRecipes = getUnavailableRecipes(availableBakedRecipes);

  return {
    product,
    availableRecipes: availableBakedRecipes,
    totalPacks: Number(totalPacks),
    defaultRecipes,
    suggestedVariant,
    unavailableRecipes,
  };
};

export const getNewPlanValues = (
  productRecommendations,
  currentFrequency,
  currentRecipes,
  recommendedProduct,
) => {
  let newVariant;

  // Find the product whose frequency matches the current plan's
  let newProduct = productRecommendations?.fresh?.products.find(product => {
    if (product.weeks_of_food !== currentFrequency) return false;

    // Check if product includes current recipes. If it doesn't, find the variant whose recipes
    // match the recommended
    if (
      difference(currentRecipes, product.available_recipes?.fresh).length !== 0
    ) {
      newVariant = getVariantByRecipes(product.variants, {
        fresh: productRecommendations.fresh.recommended.recipes.fresh,
        dry: [],
      });
      return true;
    }

    // Find the variant whose recipes match the current plan's
    newVariant = getVariantByRecipes(product.variants, {
      fresh: currentRecipes,
      dry: [],
    });

    return true;
  });

  // If no product was found that matches current frequency, try finding a product that matches current recipes.
  if (!newProduct) {
    newProduct = recommendedProduct;
    newVariant =
      getVariantByRecipes(recommendedProduct.variants, {
        fresh: currentRecipes,
        dry: [],
      }) ?? newProduct.variants[0];
  }

  // fallback to recommended
  if (!newProduct || !newVariant) {
    newProduct = recommendedProduct;
    newVariant = newProduct.variants[0];
  }

  return {
    newPricePerWeek: newVariant.price_per_week,
    newRecipes: newVariant.recipes,
    newTrayCount: newProduct.variants[0].tray_count,
    newWeeksOfFood: newProduct.weeks_of_food,
    newSku: newVariant.sku,
    newBagCount: newVariant.bag_count,
  };
};

export const getEditedPlanValues = (
  currentFrequency,
  productTypes,
  selectedFreshRecipes,
  selectedDryRecipes,
  mealPrepType,
) => {
  const selectedPrepType = productTypes[mealPrepType];
  const recommendedFrequency = selectedPrepType.products.find(product =>
    product.variants.find(
      variant => variant.sku === selectedPrepType.recommended.subscription_sku,
    ),
  )?.weeks_of_food;

  // Search for the current frequency, then the recommended product's frequency.
  const newProduct =
    selectedPrepType.products.find(
      product => product.weeks_of_food === currentFrequency,
    ) ??
    selectedPrepType.products.find(
      product => product.weeks_of_food === recommendedFrequency,
    );
  const newVariant = getVariantByRecipes(newProduct.variants, {
    fresh: selectedFreshRecipes,
    dry: selectedDryRecipes,
  });

  return {
    newProduct: newProduct,
    newPricePerWeek: newVariant.price_per_week,
    newRecipes: newVariant.recipes,
    newTrayCount: newProduct.tray_count,
    newWeeksOfFood: newProduct.weeks_of_food,
    newSku: newVariant.sku,
  };
};

export const formatPlanValuesRecipes = recipes => {
  const hasDry = recipes.dry.length > 0;
  const hasFresh = recipes.fresh.length > 0;

  if (hasFresh && hasDry) {
    return [
      ...recipes.fresh.map(recipe => `Fresh ${capitalize(recipe)}`).join(', '),
      ',',
      ' ',
      ...recipes.dry.map(recipe => `Baked ${capitalize(recipe)}`).join(', '),
    ];
  }

  if (hasFresh) {
    return recipes.fresh
      .map(recipe => `Fresh ${capitalize(recipe)}`)
      .join(', ');
  }

  if (hasDry) {
    return recipes.dry.map(recipe => `Baked ${capitalize(recipe)}`).join(', ');
  }
};

export const determinePlanType = recipes => {
  const hasDry = recipes.dry && recipes.dry.length > 0;
  const hasFresh = recipes.fresh && recipes.fresh.length > 0;

  if (hasDry && hasFresh) return 'mixed';
  if (hasDry) return 'baked';
  if (hasFresh) return 'fresh';
  throw new Error('Invalid object');
};
